// WHAT THIS DEVICE CAN AFFORD (phone-first pass, 2026-09-06).
//
// Kept in step with libs/core-unity/src/deviceTier.ts — the school app imports that copy, this
// app does not depend on libs (same split the TouchActions HUD already lives with).
//
// One number, 0..3, that the client honours everywhere expensive:
//   0 old phone · 1 phone · 2 tablet / Chromebook · 3 desktop
//
// Nobody can enumerate "older phones", so this does not try. It GUESSES from what the browser
// will actually admit, starts there, and then MEASURES the real frame time once the world is
// running and corrects itself — because the only reliable capability signal is the frame time on
// the device in front of you. `deviceMemory` is Chrome-only and the GPU string is masked in
// Safari: **unknown is never treated as weak**, or every iPhone lands on the lowest tier.
//
// The hard floor (no WebGL2 / no WASM) is NOT here — that is checkWorldCapability() in
// the school app's webglSupport probe, which decides whether there is a world at all. This only decides how rich it is.

export type DeviceTier = 0 | 1 | 2 | 3;

export const TIER_OLD_PHONE: DeviceTier = 0;
export const TIER_PHONE: DeviceTier = 1;
export const TIER_TABLET: DeviceTier = 2;
export const TIER_DESKTOP: DeviceTier = 3;

const KEY = 'gamedate.deviceTier';
const REMEMBER_MS = 30 * 24 * 60 * 60 * 1000; // a device does not change under a pupil

type NavPlus = Navigator & {
  deviceMemory?: number;
  connection?: { effectiveType?: string; saveData?: boolean };
};

function nav(): NavPlus | null {
  return typeof navigator === 'undefined' ? null : (navigator as NavPlus);
}

function coarsePointer(): boolean {
  try {
    return window.matchMedia?.('(pointer: coarse)').matches ?? false;
  } catch {
    return false;
  }
}

/** The best tier a form factor may ever be promoted to — a phone is a phone however fast it is. */
export function tierCeiling(): DeviceTier {
  if (!coarsePointer()) return TIER_DESKTOP;
  const short = Math.min(window.screen?.width ?? 1024, window.screen?.height ?? 768);
  return short >= 744 ? TIER_TABLET : TIER_PHONE; // 744 = iPad mini's short side in CSS px
}

/**
 * The prior. Phones start at 1 and drop to 0 only on a POSITIVE weakness signal (data saver, 2g,
 * ≤2 GB, ≤2 cores) — an absent signal is not evidence. Everything else is corrected by measurement.
 */
export function guessDeviceTier(): DeviceTier {
  const n = nav();
  const cores = n?.hardwareConcurrency ?? 0;
  const mem = n?.deviceMemory ?? 0;
  const conn = n?.connection;
  const thrifty = conn?.saveData === true || /(^|[^3-9])2g$/.test(conn?.effectiveType ?? '');

  if (!coarsePointer()) return cores > 0 && cores <= 2 ? TIER_TABLET : TIER_DESKTOP;

  const ceiling = tierCeiling();
  const weak = thrifty || (mem > 0 && mem <= 2) || (cores > 0 && cores <= 2);
  if (weak) return TIER_OLD_PHONE;
  return ceiling === TIER_TABLET ? TIER_TABLET : TIER_PHONE;
}

/** What we measured on this device last time, if it is still fresh. */
export function rememberedDeviceTier(): DeviceTier | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { tier?: number; at?: number };
    if (typeof parsed?.tier !== 'number' || typeof parsed?.at !== 'number') return null;
    if (Date.now() - parsed.at > REMEMBER_MS) return null;
    return Math.max(0, Math.min(3, parsed.tier)) as DeviceTier;
  } catch {
    return null;
  }
}

export function rememberDeviceTier(tier: DeviceTier): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ tier, at: Date.now() }));
  } catch {
    /* private mode — the guess still works, it just re-measures next time */
  }
}

/** What to start the session with: last measurement if we have one, else the prior. */
export function initialDeviceTier(): DeviceTier {
  return rememberedDeviceTier() ?? guessDeviceTier();
}

function median(values: number[]): number {
  const v = [...values].sort((a, b) => a - b);
  return v.length ? v[Math.floor(v.length / 2)] : 0;
}

/**
 * Watch the real frame rate for a few seconds and correct the tier ONCE.
 *
 * The page's requestAnimationFrame is the same loop Unity renders in, so a frame the GPU cannot
 * afford stretches this too. Demote below 24 fps, promote above 50 (never past the form factor's
 * ceiling), and remember the answer so the next lesson starts right.
 *
 * Returns a cancel function; safe to call before the world is ready (the settle window covers the
 * scene build, which is always the worst frames of the session).
 */
export function measureDeviceTier(
  from: DeviceTier,
  onCorrected: (tier: DeviceTier) => void,
  opts?: { settleMs?: number; sampleMs?: number },
): () => void {
  if (typeof requestAnimationFrame !== 'function') return () => undefined;

  const settleMs = opts?.settleMs ?? 2500;
  const sampleMs = opts?.sampleMs ?? 4000;
  const ceiling = tierCeiling();
  const deltas: number[] = [];
  const t0 = performance.now();
  let last = 0;
  let handle = 0;
  let stopped = false;

  const tick = (t: number) => {
    if (stopped) return;
    const elapsed = t - t0;
    if (last > 0 && elapsed > settleMs) {
      const d = t - last;
      if (d < 250) deltas.push(d); // a tab switch or a GC pause is not a frame rate
    }
    last = t;

    if (elapsed < settleMs + sampleMs) {
      handle = requestAnimationFrame(tick);
      return;
    }

    const fps = deltas.length >= 20 ? 1000 / median(deltas) : 0;
    let next = from;
    if (fps > 0 && fps < 24 && from > TIER_OLD_PHONE) next = (from - 1) as DeviceTier;
    else if (fps > 50 && from < ceiling) next = (from + 1) as DeviceTier;

    rememberDeviceTier(next);
    if (next !== from) onCorrected(next);
  };

  handle = requestAnimationFrame(tick);
  return () => {
    stopped = true;
    if (handle) cancelAnimationFrame(handle);
  };
}
